import { config } from "../config.js";
import { nextRandom, randomRange } from "./rng.js";
import { stepGimmicks } from "./stage-rules.js";
import { scaleAt, fieldOf, radiusOf } from "./scale.js";
import { useMission, mission } from "./missions.js";
import { WEAPONS, rollChoices, applyChoice, weaponStats, passiveValue, maxShield, heal, attackPower } from "./upgrades.js";

// 試合の状態(world)と、それを1ステップ進める step()。
// world は JSON にできる素のデータだけを持つ(three.js の物や関数を入れない)。描画(src/view/)は world を見て描くだけ。
// 同じ seed・同じ入力の列なら、必ず同じ展開になる(リプレイ・R.W.N.D.の前提)。
//
// 座標(レール空間): 自機は z=0 の面の中(x: 左右、y: 上下)を動く。前は z−。画面は自動で前へ進んで見えるが、
// 計算では自機は z=0 のまま、敵・弾・経験値が動く。
// スケール感: 空間の広さ・敵の大きさや速さには world.scale(src/sim/scale.js)を掛ける。広さは config.field ではなく fieldOf(world) で読む。

const DT = 1 / config.sim.tickRate;

// params: 試合の前に決まり、試合中は変わらない値(リプレイに一緒に残す)
// meta: 格納庫の永続強化(src/meta/hangar.js の bonusesFor)。倍率はどれも 1 で強化なし
export function createWorld({ seed = 1, maxAlive = config.enemies.maxAlive.pc, meta = {}, mission: missionNo = 3 } = {}) {
  useMission(missionNo);
  const maxShieldValue = config.player.maxShield * (meta.shield || 1);
  return {
    version: config.sim.version,
    tick: 0,
    time: 0, // 試合の中の経過秒(SLOW中はゆっくり進む)
    progress: 0, // 前進した秒(停止戦の間は進まない)。ステージ・敵の出現・クリアはこれで決まる
    scroll: 1, // 今の前進の速さ(0=停止〜1)
    scale: scaleAt(0), // スケール感の倍率(src/sim/scale.js)
    hold: null, // 停止戦の最中なら { index, elapsed, boss }
    nextHold: 0,
    rngState: seed | 0,
    nextId: 1,
    params: { seed, maxAlive, mission: missionNo, maxShield: maxShieldValue, power: meta.power || 1, xpGain: meta.xpGain || 1, magnet: meta.magnet || 1 },
    player: {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      shield: maxShieldValue,
      invuln: 0,
      powerBoost: 0, // 出力ブーストの残り秒(R.W.N.D.の救済)
      level: 1,
      xp: 0,
      weapons: { beam: 1 },
      passives: {},
      cooldowns: { beam: 0 },
      saberAngle: 0,
      mainGunFiring: 0, // 主砲を撃っている残り秒
    },
    enemies: [],
    shots: [], // 自機の弾
    enemyShots: [],
    gems: [],
    repairs: [], // 修理カプセル { x, y, z }
    hazards: [], // ステージの仕掛け(レーザーの柵・対空砲。src/sim/stage-rules.js)
    gimmickAcc: 0,
    spawnAcc: 0,
    bossSpawned: false,
    slowTicks: 0,
    pendingLevelUp: null, // { choices: [...] } 選ぶまで step は進まない
    over: null, // null | "dead" | "clear"
    stats: { kills: 0, score: 0, damageTaken: 0, holdsFailed: 0 }, // holdsFailed: 時間切れで敵を残して終わった停止戦の数(面ごとの目標「全滅」に使う)
    events: [], // このステップで起きた出来事(描画・音が使う。次のステップの始めに空にする)
  };
}

// input: { mx, my } は −100〜100 の整数(移動の向き)。choice はレベルアップで選んだ番号(選ぶステップだけ)
// R.W.N.D.で再開した最初のステップに入る救済(入力の rewind: { heal, power })。入力として記録するのでリプレイも同じになる
function applyRewindRescue(world, r) {
  const p = world.player;
  p.invuln = Math.max(p.invuln, config.rewind.invulnSeconds);
  if (r.heal) heal(world, maxShield(world), "rewind");
  if (r.power) p.powerBoost = config.rewind.boosts.power.seconds;
  world.events.push({ k: "rescue", heal: !!r.heal, power: !!r.power });
}

export function step(world, input = {}) {
  if (world.over) return false;
  useMission(world.params.mission || 3);
  world.events = [];
  if (world.pendingLevelUp) {
    if (input.choice == null) return false;
    const c = world.pendingLevelUp.choices[input.choice] || world.pendingLevelUp.choices[0];
    applyChoice(world, c);
    world.pendingLevelUp = null;
    world.events.push({ k: "chosen", choice: c });
    startSlow(world, config.slow.levelUpSeconds);
  }
  if (input.rewind) applyRewindRescue(world, input.rewind);

  const slow = world.slowTicks > 0;
  if (slow) {
    world.slowTicks--;
    if (world.slowTicks === 0) world.events.push({ k: "slowEnd" });
  }
  const dt = DT * (slow ? config.slow.scale : 1);
  world.time += dt;
  stepHold(world, dt);
  world.scale = scaleAt(world.progress);

  stepPlayer(world, input, dt);
  stepSpawner(world, dt);
  stepGimmicks(world, dt, { spawnEnemy, hurtPlayer });
  stepEnemies(world, dt);
  stepWeapons(world, dt);
  stepShots(world, dt);
  stepEnemyShots(world, dt);
  stepGems(world, dt);
  stepRepairs(world, dt);
  cleanup(world);

  if (world.player.shield <= 0) {
    world.player.shield = 0;
    world.over = "dead";
    world.events.push({ k: "dead" });
  } else if ((world.progress >= mission().runSeconds || world.finaleDone) && !world.hold) {
    world.over = "clear";
    world.events.push({ k: "clear" });
  }
  world.tick++;
  return true;
}

// 停止戦: 前進を止め、囲んでくる敵の群れを出す。群れ(hold の付いた敵)が全部いなくなったら再開
function stepHold(world, dt) {
  const hc = config.holds;
  const ease = dt / hc.easeSeconds;
  if (world.hold) {
    world.scroll = Math.max(0, world.scroll - ease);
    world.hold.elapsed += dt;
    const left = world.enemies.some((e) => e.hold && e.hp > 0);
    // final(その面の最後の大物)は、主役(leader)が残っている間は時間切れなし
    const leaderLeft = world.hold.final && world.enemies.some((e) => e.leader && e.hp > 0);
    if (!left || (!leaderLeft && world.hold.elapsed >= hc.maxSeconds)) {
      if (!left) holdBonus(world);
      else world.stats.holdsFailed++;
      for (const e of world.enemies) {
        // 時間切れで残った母艦は引き上げる(後から来る最後の大玉の前をふさがないように。2026-11-01)
        if (world.hold.boss && e.leader && e.hp > 0) e.leave = 1;
        e.hold = 0;
      }
      world.events.push({ k: "holdClear", cleared: !left, boss: world.hold.boss });
      if (world.hold.final) world.finaleDone = true; // 面の最後の大物を倒したら、残りの時間を待たずにクリア(2026-11-01 オーナー要望「時間内に倒せるように」)
      world.hold = null;
    }
    return;
  }
  world.scroll = Math.min(1, world.scroll + ease);
  world.progress += dt * world.scroll;
  const m = mission();
  const next = m.holds[world.nextHold];
  if (next && world.progress >= next.at) {
    world.nextHold++;
    world.hold = { index: world.nextHold - 1, elapsed: 0, boss: false, final: !!next.final };
    spawnWave(world, next.wave);
    if (next.leader) {
      // 停止戦の主役(中ボス)。前の遠くから来る
      const s = world.scale;
      const leader = spawnEnemy(world, next.leader, 0, 10 * s, (config.field.spawnFrontZ - 100) * s);
      leader.hold = 1;
      leader.leader = 1;
      // 護衛のドローンポッド(大玉。config.enemies.types.escort)
      const n = config.enemies.types[next.leader].escorts || 0;
      for (let i = 0; i < n; i++) {
        const d = spawnEnemy(world, "escort", leader.x, leader.y, leader.z);
        d.host = leader.id;
        d.slot = i;
        d.hold = 1;
        d.cd = config.enemies.types.escort.fireSeconds * (1 + i / n); // 6機が一度に撃たないよう、ずらす
      }
    }
    world.events.push({ k: "holdStart", index: world.hold.index });
  } else if (!world.bossSpawned && m.bossAt != null && world.progress >= m.bossAt) {
    world.bossSpawned = true;
    world.hold = { index: -1, elapsed: 0, boss: true, final: m.bossFinal };
    const s = world.scale;
    const boss = spawnEnemy(world, "boss", 0, 10 * s, (config.enemies.types.boss.holdZ - 150) * s); // 持ち場(holdZ)の少し奥から
    boss.hold = 1;
    boss.leader = 1;
    world.events.push({ k: "boss" });
  }
}

// 停止戦の群れ: 前と左右から、輪を作って寄ってくる
function spawnWave(world, wave) {
  const f = fieldOf(world);
  const s = world.scale;
  for (const [type, n] of Object.entries(wave)) {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + nextRandom(world) * 0.3;
      const side = i % 3; // 0: 前、1: 左、2: 右
      let x;
      let z;
      if (side === 0 || type === "gunner") {
        x = Math.cos(a) * f.halfWidth * 1.4;
        z = f.spawnFrontZ * 0.7 + randomRange(world, -80, 40) * s;
      } else {
        x = (side === 1 ? -1 : 1) * (f.spawnSideX + randomRange(world, 0, 60) * s);
        z = randomRange(world, -200, -20) * s;
      }
      const e = spawnEnemy(world, type, x, Math.sin(a) * f.halfHeight * 1.2, z);
      e.hold = 1;
    }
  }
}

function holdBonus(world) {
  const b = config.holds.clearBonus;
  const p = world.player;
  const s = world.scale;
  for (let i = 0; i < b.repair; i++) world.repairs.push({ x: p.x + randomRange(world, -20, 20) * s, y: p.y + randomRange(world, -10, 10) * s, z: -160 * s });
  for (let i = 0; i < b.gems; i++) world.gems.push({ x: p.x + randomRange(world, -40, 40) * s, y: p.y + randomRange(world, -25, 25) * s, z: randomRange(world, -120, -40) * s, v: 3, pull: 1 });
}

function startSlow(world, seconds) {
  if (world.slowTicks <= 0) world.events.push({ k: "slowStart" });
  world.slowTicks = Math.max(world.slowTicks, Math.round(seconds * config.sim.tickRate));
}

function clampInput(v) {
  return Math.max(-100, Math.min(100, v | 0)) / 100;
}

function stepPlayer(world, input, dt) {
  const p = world.player;
  let mx = clampInput(input.mx || 0);
  let my = clampInput(input.my || 0);
  const len = Math.hypot(mx, my);
  if (len > 1) {
    mx /= len;
    my /= len;
  }
  const grow = 1 + (world.scale - 1) * config.scale.playerSpeedFollow; // 広くなった範囲を動き回れるように
  const speed = config.player.speed * passiveValue(world, "thruster") * grow;
  const f = fieldOf(world);
  p.vx = mx * speed;
  p.vy = my * speed;
  p.x = Math.max(-f.halfWidth, Math.min(f.halfWidth, p.x + p.vx * dt));
  p.y = Math.max(-f.halfHeight, Math.min(f.halfHeight, p.y + p.vy * dt));
  if (p.invuln > 0) p.invuln -= dt;
  if (p.powerBoost > 0) p.powerBoost = Math.max(0, p.powerBoost - dt);
  if (config.player.regenPerSecond) p.shield = Math.min(maxShield(world), p.shield + config.player.regenPerSecond * dt);
}

// 時間に応じた毎秒の出現数(config.enemies.spawnCurve を直線でつなぐ)
export function spawnRate(t) {
  const curve = mission().spawnCurve;
  for (let i = 1; i < curve.length; i++) {
    if (t <= curve[i][0]) {
      const [t0, r0] = curve[i - 1];
      const [t1, r1] = curve[i];
      return r0 + ((r1 - r0) * (t - t0)) / (t1 - t0);
    }
  }
  return curve[curve.length - 1][1];
}

function stepSpawner(world, dt) {
  const ec = config.enemies;
  if (world.hold) return; // 停止戦の間は、いつもの出現を止める
  world.spawnAcc += spawnRate(world.progress) * mission().enemy.spawn * dt;
  while (world.spawnAcc >= 1) {
    world.spawnAcc -= 1;
    if (world.enemies.length >= world.params.maxAlive) continue;
    const type = rosterPick(world, nextRandom(world));
    const f = fieldOf(world);
    const s = world.scale;
    if (nextRandom(world) < ec.sideRatio && type !== "gunner") {
      const side = nextRandom(world) < 0.5 ? -1 : 1;
      spawnEnemy(world, type, side * f.spawnSideX, randomRange(world, -f.halfHeight, f.halfHeight), randomRange(world, -160, -40) * s);
    } else {
      spawnEnemy(world, type, randomRange(world, -f.halfWidth * 1.6, f.halfWidth * 1.6), randomRange(world, -f.halfHeight * 1.5, f.halfHeight * 1.5), f.spawnFrontZ + randomRange(world, -60, 0) * s);
    }
  }
}

// いつもの出現で出す種類(config.enemies.roster。出る順は前進の秒で決まる)。r: 0〜1 の乱数
export function rosterPick(world, r) {
  let acc = 0;
  for (const it of mission().roster) {
    if (world.progress < it.from) continue;
    acc += it.ratio;
    if (r < acc) return it.type;
  }
  return "pod";
}

// スケール感: reach = 出たときの倍率(速さ・動く幅に掛ける)。size = 大きさ(当たり判定・見た目) = reach × sizeMul(大物だけ)。
// 体力は reach × hpSize で増やす(大物を見やすく大きくしても、体力は変わらないように)
function spawnEnemy(world, type, x, y, z) {
  const t = config.enemies.types[type];
  const reach = world.scale;
  const size = reach * (t.sizeMul || 1);
  const hpSize = reach * (t.hpSize ?? t.sizeMul ?? 1);
  const hp = t.hp * mission().enemy.hp * (1 + config.enemies.hpGrowthPerMinute * mission().enemy.growth * (world.progress / 60)) * (1 + (hpSize - 1) * config.scale.hpPerSize);
  const e = { id: world.nextId++, type, x, y, z, vx: 0, vy: 0, vz: 0, hp, maxHp: hp, cd: t.fireSeconds || 0, state: 0, saberHit: 0, age: 0, hold: 0, reach, size };
  world.enemies.push(e);
  return e;
}

function stepEnemies(world, dt) {
  const p = world.player;
  const pr = config.player.radius;
  const flowSpeed = fieldOf(world).scrollSpeed;
  for (const e of world.enemies) {
    const t = config.enemies.types[e.type];
    const k = e.reach; // 倍率が大きいときに出た敵ほど速い(届くまでの秒は変わらない)
    const sp = t.speed * k;
    e.age += dt;
    const dx = p.x - e.x;
    const dy = p.y - e.y;
    const dz = 0 - e.z;
    const d = Math.hypot(dx, dy, dz) || 1;
    if (e.type === "satellite" || e.type === "rock") {
      // 仕掛け: 前から流れてくるだけ
      e.vz = flowSpeed * (e.type === "rock" ? 1 : 0.6) * Math.max(0.3, world.scroll);
    } else if (e.type === "mine") {
      // 機雷: ゆっくり寄ってきて、近くで止まり、少し待って爆発する(撃ち落とせば爆発で周りの敵を巻き込む)
      if (e.state === 0) {
        steer(e, (dx / d) * sp, (dy / d) * sp, (dz / d) * sp, 1.5 * dt);
        if (d < t.armRange * k) {
          e.state = 1;
          e.cd = t.fuse;
          world.events.push({ k: "mineArm", id: e.id });
        }
      } else {
        steer(e, 0, 0, 0, 4 * dt);
        e.cd -= dt;
        if (e.cd <= 0) {
          e.hp = 0;
          mineBlast(world, e, true);
          continue;
        }
      }
    } else if (e.type === "pod" || e.type === "splitter" || e.type === "shard") {
      // 自機へ寄ってくる。近くまで来たら曲がらない(動けば避けられる)
      if (e.hold && e.z > -25 * k) {
        // 停止戦の敵は、通り過ぎたら前へ戻って、また向かってくる
        steer(e, (dx / d) * sp * 0.5, (dy / d) * sp * 0.5, -sp, 1.2 * dt);
      } else if (e.z < -40 * k || e.age < 0.5 || e.hold) steer(e, (dx / d) * sp, (dy / d) * sp, (dz / d) * sp, (e.hold ? 0.9 : 0.6) * dt);
    } else if (e.type === "dasher") {
      if (e.hold && e.state === 1 && e.z > 30 * k) e.state = 0; // 停止戦: 通り過ぎたら、もう一度向かってくる
      if (e.state === 0) {
        steer(e, (dx / d) * sp * 0.4, (dy / d) * sp * 0.4, (dz / d) * sp * 0.4, 2 * dt);
        if (d < 170 * k && !(e.hold && e.z > -60 * k)) {
          e.state = 1; // 突撃開始: この時点の自機の位置へまっすぐ
          e.vx = (dx / d) * sp * 1.6;
          e.vy = (dy / d) * sp * 1.6;
          e.vz = (dz / d) * sp * 1.6;
        }
      }
    } else if (e.type === "escort") {
      // 護衛のドローンポッド: 大玉のまわりの輪を回りながら、自機を狙って撃つ
      const host = world.enemies.find((h) => h.id === e.host && h.hp > 0);
      if (host) {
        const ht = config.enemies.types[host.type];
        const a = (e.slot / ht.escorts + world.time / ht.escortSpinSeconds) * Math.PI * 2;
        const ring = ht.radius * host.size * ht.escortRing;
        const tx = host.x + Math.cos(a) * ring;
        const ty = host.y + Math.sin(a) * ring;
        e.vx = host.vx + (tx - e.x) * 3;
        e.vy = host.vy + (ty - e.y) * 3;
        e.vz = host.vz + (host.z - e.z) * 3;
        e.cd -= dt;
        if (e.cd <= 0 && host.z > config.enemies.types[host.type].holdZ * host.reach - 80 * host.reach) {
          e.cd = t.fireSeconds;
          fireEnemyShot(world, e, t);
        }
      }
    } else {
      // 射撃型・母艦・中ボス: holdZ まで来て止まり、横に揺れながら撃つ
      const targetZ = t.holdZ * k;
      if (e.leave) {
        // 引き上げ: 撃たずに奥へ下がり、見えなくなったら消える(倒したことにはしない)
        e.cc = null;
        steer(e, 0, 0, -sp * 12, 1 * dt);
        if (e.z < targetZ * 3) e.hp = 0;
        e.x += e.vx * dt;
        e.y += e.vy * dt;
        e.z += e.vz * dt;
        continue;
      }
      const inPlace = e.z > targetZ - 80 * k;
      const charging = t.cannon && stepCannon(world, e, t, inPlace, dt);
      if (charging) {
        steer(e, 0, 0, 0, 3 * dt); // チャージ中・撃っている間は止まる(狙いが読めるように)
      } else {
        const sway = (t.big ? Math.sin(e.age * 0.5) * 40 : Math.sin(e.age * 0.9 + e.id) * 20) * k;
        const tvz = Math.max(-sp, Math.min(sp * 3, (targetZ - e.z) * 0.8));
        steer(e, (sway - e.x) * 0.5, (p.y * 0.5 + 10 * k - e.y) * 0.4, tvz, 2 * dt);
        e.cd -= dt;
        if (e.cd <= 0 && inPlace) {
          e.cd = t.fireSeconds;
          fireEnemyShot(world, e, t);
        }
      }
    }
    e.x += e.vx * dt;
    e.y += e.vy * dt;
    e.z += e.vz * dt;
    // 体当たり
    if (t.damage && p.invuln <= 0 && Math.hypot(p.x - e.x, p.y - e.y, e.z) < pr + radiusOf(e)) {
      hurtPlayer(world, t.damage, e);
      if (!t.big) {
        e.hp = 0; // ぶつかった雑魚は壊れる(経験値は落とさない)
        world.events.push({ k: "kill", x: e.x, y: e.y, z: e.z, type: e.type, big: false, crash: true });
      }
    }
  }
}

function steer(e, tvx, tvy, tvz, k) {
  const a = Math.min(1, k);
  e.vx += (tvx - e.vx) * a;
  e.vy += (tvy - e.vy) * a;
  e.vz += (tvz - e.vz) * a;
}

// チャージ主砲(config.enemies.cannon)。e.cn: 次を撃つまでの秒、e.cc: { t: チャージを始めてからの秒, ax・ay: 狙い(自機の面 z=0 の位置), hit } またはチャージしていない間は無し。
// チャージ中か撃っている間なら true を返す
function stepCannon(world, e, t, inPlace, dt) {
  const c = { ...config.enemies.cannon, ...t.cannon };
  const p = world.player;
  if (!e.cc) {
    if (!inPlace) return false;
    if (e.cn === undefined) e.cn = c.first;
    e.cn -= dt;
    if (e.cn > 0) return false;
    e.cc = { t: 0, ax: p.x, ay: p.y, hit: 0 };
    world.events.push({ k: "cannonCharge", id: e.id });
  }
  const cc = e.cc;
  cc.t += dt;
  if (cc.t < c.charge - c.lock) {
    // 狙いは自機を追う(少し遅れて付いてくる)
    cc.ax += (p.x - cc.ax) * Math.min(1, 6 * dt);
    cc.ay += (p.y - cc.ay) * Math.min(1, 6 * dt);
  }
  if (cc.t >= c.charge) {
    if (cc.t - dt < c.charge) world.events.push({ k: "cannonFire", id: e.id, ax: cc.ax, ay: cc.ay });
    if (!cc.hit && p.invuln <= 0 && Math.hypot(p.x - cc.ax, p.y - cc.ay) < c.halfWidth * e.reach + config.player.radius) {
      cc.hit = 1;
      hurtPlayer(world, c.damage, { x: cc.ax, y: cc.ay, z: e.z });
    }
    if (cc.t >= c.charge + c.fire) {
      e.cc = null;
      e.cn = c.every;
      e.cd = t.fireSeconds; // いつもの弾は少し間をあけてから
      return false;
    }
  }
  return true;
}

function fireEnemyShot(world, e, t) {
  const p = world.player;
  const shots = t.shots || 1;
  const k = e.reach; // 倍率が大きいときの弾は速い(遠くから撃つので、届くまでの秒はほぼ同じ)
  const dx = p.x - e.x;
  const dy = p.y - e.y;
  const dz = -e.z;
  const d = Math.hypot(dx, dy, dz) || 1;
  for (let i = 0; i < shots; i++) {
    const spread = shots > 1 ? (i - (shots - 1) / 2) * 0.09 : 0;
    const vx = (dx / d + spread) * t.shotSpeed * k;
    const vy = ((dy / d) * t.shotSpeed + (shots > 1 ? Math.sin(world.time * 3 + i) * 6 : 0)) * k;
    const vz = (dz / d) * t.shotSpeed * k;
    world.enemyShots.push({ x: e.x, y: e.y, z: e.z + (t.muzzleZ ? t.muzzleZ * e.size : 4 * k), vx, vy, vz, life: 12 });
  }
  world.events.push({ k: "enemyFire", x: e.x, y: e.y, z: e.z, boss: !!t.big, mz: t.muzzleZ ? e.z + t.muzzleZ * e.size : e.z, size: e.size }); // mz: 弾の出る所(描画用)
}

// from: 当てた物(敵や弾)。どこから当たったかを画面に出すため、位置を出来事に入れる
function hurtPlayer(world, dmg, from) {
  const p = world.player;
  if (p.invuln > 0) return;
  dmg *= mission().enemy.damage;
  p.shield -= dmg;
  p.invuln = config.player.hitInvulnSeconds;
  world.stats.damageTaken += dmg;
  world.events.push({ k: "hurt", dmg, fx: from.x, fy: from.y, fz: from.z });
}

function nearestEnemy(world, x, y, z, maxZ = -4) {
  let best = null;
  let bestD = Infinity;
  for (const e of world.enemies) {
    if (e.hp <= 0 || e.z > maxZ) continue;
    const d = (e.x - x) ** 2 + (e.y - y) ** 2 + (e.z - z) ** 2;
    if (d < bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

function stepWeapons(world, dt) {
  const p = world.player;
  const power = attackPower(world);
  const haste = passiveValue(world, "haste");
  const reach = world.scale; // 弾の速さ(広くなった空間でも、敵に届くまでの秒を変えない)。当たりの大きさには掛けない
  for (const id of Object.keys(p.weapons)) {
    const s = weaponStats(world, id);
    if (id === "saber") {
      stepSaber(world, s, dt, power);
      continue;
    }
    p.cooldowns[id] -= dt;
    if (p.cooldowns[id] > 0) continue;
    if (id === "beam") {
      const target = nearestEnemy(world, p.x, p.y, 0);
      if (!target) continue;
      for (let i = 0; i < s.count; i++) {
        const off = (i - (s.count - 1) / 2) * 2.2;
        const dx = target.x - (p.x + off);
        const dy = target.y - p.y;
        const dz = target.z;
        const d = Math.hypot(dx, dy, dz) || 1;
        world.shots.push({ kind: "beam", x: p.x + off, y: p.y, z: -4, vx: (dx / d) * s.speed * reach, vy: (dy / d) * s.speed * reach, vz: (dz / d) * s.speed * reach, dmg: s.damage * power, r: 1.6 * s.size, size: s.size, life: 1.6 });
      }
      world.events.push({ k: "fire", w: "beam" });
    } else if (id === "spread") {
      // 自機を中心に、輪(リップル)になって広がりながら前へ飛ぶ(2026-10-29 オーナー要望。前は水平の扇形)。
      // angle は前の向きからの開き(ラジアン)。撃つたびに輪を少し回して、すき間の位置を変える
      const speed = 340 * reach;
      const twist = (world.tick % 7) / 7;
      for (let i = 0; i < s.count; i++) {
        const a = ((i + twist) / s.count) * Math.PI * 2;
        const out = Math.sin(s.angle) * speed;
        world.shots.push({ kind: "spread", x: p.x, y: p.y, z: -4, vx: Math.cos(a) * out, vy: Math.sin(a) * out, vz: -Math.cos(s.angle) * speed, dmg: s.damage * power, r: 1.8 * s.size, size: s.size, life: 1.4 });
      }
      world.events.push({ k: "fire", w: "spread", x: p.x, y: p.y, angle: s.angle });
    } else if (id === "missile") {
      for (let i = 0; i < s.count; i++) {
        const a = (i / Math.max(1, s.count) + nextRandom(world) * 0.2) * Math.PI * 2;
        world.shots.push({ kind: "missile", x: p.x, y: p.y, z: -2, vx: Math.cos(a) * 60 * reach, vy: (Math.sin(a) * 60 + 30) * reach, vz: -40 * reach, dmg: s.damage * power, blast: s.blast, r: 2, life: 4, target: 0 });
      }
      world.events.push({ k: "fire", w: "missile" });
    } else if (id === "mainGun") {
      p.mainGunFiring = 0.45;
      mainGunHit(world, s, power);
      world.events.push({ k: "mainGun", x: p.x, y: p.y, r: s.radius });
    }
    p.cooldowns[id] = s.cooldown * haste;
  }
  if (p.mainGunFiring > 0) p.mainGunFiring -= dt;
}

function mainGunHit(world, s, power) {
  const p = world.player;
  for (const e of world.enemies) {
    if (e.z > 0 || e.hp <= 0) continue;
    if (Math.hypot(e.x - p.x, e.y - p.y) < s.radius + radiusOf(e)) damageEnemy(world, e, s.damage * power);
  }
}

function stepSaber(world, s, dt, power) {
  const p = world.player;
  p.saberAngle += s.spin * dt;
  const hitGap = 0.35;
  for (let i = 0; i < s.count; i++) {
    const a = p.saberAngle + (i / s.count) * Math.PI * 2;
    // 刃は自機のまわりを x-z の面で回る(横と前の敵を斬る)
    const bx = p.x + Math.cos(a) * s.radius;
    const bz = Math.sin(a) * s.radius;
    for (const e of world.enemies) {
      if (e.hp <= 0 || world.time - e.saberHit < hitGap) continue;
      if (Math.hypot(e.x - bx, e.y - p.y, e.z - bz) < radiusOf(e) + 5) {
        e.saberHit = world.time;
        damageEnemy(world, e, s.damage * power);
        world.events.push({ k: "saberHit", x: e.x, y: e.y, z: e.z });
      }
    }
  }
}

function stepShots(world, dt) {
  const byId = new Map(world.enemies.map((e) => [e.id, e]));
  for (const s of world.shots) {
    s.life -= dt;
    if (s.kind === "missile") {
      let target = byId.get(s.target);
      if (!target || target.hp <= 0) {
        target = nearestEnemy(world, s.x, s.y, s.z, 10);
        s.target = target ? target.id : 0;
      }
      if (target) {
        const dx = target.x - s.x;
        const dy = target.y - s.y;
        const dz = target.z - s.z;
        const d = Math.hypot(dx, dy, dz) || 1;
        const sp = 230 * world.scale;
        const k = Math.min(1, 5 * dt);
        s.vx += ((dx / d) * sp - s.vx) * k;
        s.vy += ((dy / d) * sp - s.vy) * k;
        s.vz += ((dz / d) * sp - s.vz) * k;
      }
    }
    s.x += s.vx * dt;
    s.y += s.vy * dt;
    s.z += s.vz * dt;
    for (const e of world.enemies) {
      if (e.hp <= 0) continue;
      const er = radiusOf(e);
      if (Math.abs(e.z - s.z) > er + s.r + 8 * world.scale) continue;
      if ((e.x - s.x) ** 2 + (e.y - s.y) ** 2 + (e.z - s.z) ** 2 < (er + s.r) ** 2) {
        if (s.kind === "missile") {
          world.events.push({ k: "blast", x: s.x, y: s.y, z: s.z, r: s.blast });
          for (const o of world.enemies) {
            if (o.hp > 0 && Math.hypot(o.x - s.x, o.y - s.y, o.z - s.z) < s.blast + radiusOf(o)) damageEnemy(world, o, s.dmg * weakFactor(o, s));
          }
        } else {
          damageEnemy(world, e, s.dmg * weakFactor(e, s));
          world.events.push({ k: "hit", x: s.x, y: s.y, z: s.z });
        }
        s.life = 0;
        break;
      }
    }
  }
}

// 大玉の弱点(上下の帯)に当たったら、ダメージを何倍にするか。at: 当たった物の位置
function weakFactor(e, at) {
  const t = config.enemies.types[e.type];
  if (!t.weakDamage) return 1;
  const d = Math.hypot(at.x - e.x, at.y - e.y, at.z - e.z) || 1;
  return Math.abs(at.y - e.y) / d > t.weakLatitude ? t.weakDamage : 1;
}

function damageEnemy(world, e, dmg) {
  if (e.hp <= 0) return;
  e.hp -= dmg;
  world.events.push({ k: "dmg", id: e.id, x: e.x, y: e.y, z: e.z, v: dmg, last: e.hp <= 0 }); // ダメージの数字(描画だけが使う。last: とどめ)
  if (e.hp > 0) return;
  const t = config.enemies.types[e.type];
  world.stats.kills++;
  world.stats.score += t.score;
  const big = !!t.big;
  world.events.push({ k: "kill", x: e.x, y: e.y, z: e.z, type: e.type, big, size: e.size });
  if (big) {
    startSlow(world, config.slow.bossKillSeconds);
    const k = e.size;
    for (let i = 0; i < 30; i++) world.gems.push({ x: e.x + randomRange(world, -30, 30) * k, y: e.y + randomRange(world, -20, 20) * k, z: e.z + randomRange(world, -20, 20) * k, v: 2 });
  } else if (t.gems) {
    // 補給衛星: 経験値をばらまく
    for (let i = 0; i < t.gems; i++) world.gems.push({ x: e.x + randomRange(world, -10, 10) * e.size, y: e.y + randomRange(world, -8, 8) * e.size, z: e.z, v: 2 });
  } else {
    world.gems.push({ x: e.x, y: e.y, z: e.z, v: t.xp });
  }
  const chance = big ? 1 : config.repair.dropChance[e.type] || 0;
  if (chance && nextRandom(world) < chance) world.repairs.push({ x: e.x, y: e.y, z: e.z });
  if (e.type === "splitter") {
    // 分裂型: 小さな2機に分かれる
    for (const s of [-1, 1]) {
      const c = spawnEnemy(world, "shard", e.x + s * 4 * e.size, e.y, e.z);
      c.vx = s * 30 * e.reach;
      c.vz = e.vz;
      c.hold = e.hold;
    }
  } else if (e.type === "mine") mineBlast(world, e, false);
  // 大玉が落ちたら、護衛のドローンポッドも一緒に爆発する
  if (big) for (const d of world.enemies) if (d.host === e.id && d.hp > 0) damageEnemy(world, d, d.hp);
}

// 機雷の爆発。周りの敵を巻き込む。自分で爆発したときだけ自機にも当たる(撃ち落としたときは当たらない)
function mineBlast(world, e, hurtsPlayer) {
  const t = config.enemies.types.mine;
  const blast = t.blast * e.size;
  world.events.push({ k: "blast", x: e.x, y: e.y, z: e.z, r: blast, mine: true });
  for (const o of world.enemies) {
    if (o !== e && o.hp > 0 && Math.hypot(o.x - e.x, o.y - e.y, o.z - e.z) < blast + radiusOf(o)) damageEnemy(world, o, t.blastDamage);
  }
  const p = world.player;
  if (hurtsPlayer && Math.hypot(p.x - e.x, p.y - e.y, e.z) < blast + config.player.radius) hurtPlayer(world, t.damage, e);
}

function stepEnemyShots(world, dt) {
  const p = world.player;
  const r = config.player.radius + config.enemies.shotRadius;
  for (const s of world.enemyShots) {
    s.life -= dt;
    s.x += s.vx * dt;
    s.y += s.vy * dt;
    s.z += s.vz * dt;
    if (p.invuln <= 0 && (s.x - p.x) ** 2 + (s.y - p.y) ** 2 + s.z ** 2 < r * r) {
      hurtPlayer(world, config.enemies.shotDamage, { x: s.x - s.vx * 0.3, y: s.y - s.vy * 0.3, z: s.z - s.vz * 0.3 });
      s.life = 0;
    }
  }
}

// 修理カプセル: 前から流れてくる(吸い寄せない。自分で取りに行く)
function stepRepairs(world, dt) {
  const p = world.player;
  const flow = fieldOf(world).scrollSpeed * 0.8 * Math.max(0.15, world.scroll);
  const r = config.player.radius + config.repair.radius + 1;
  const ry = r * config.repair.reachYRatio; // 上下だけ広い(縦長の楕円体で判定する)
  for (const c of world.repairs) {
    c.z += flow * dt;
    if (((c.x - p.x) ** 2 + c.z * c.z) / (r * r) + (c.y - p.y) ** 2 / (ry * ry) < 1) {
      c.taken = 1;
      heal(world, config.repair.amount, "repair");
    }
  }
}

function stepGems(world, dt) {
  const p = world.player;
  const magnet = config.player.magnetRadius * passiveValue(world, "magnet") * world.params.magnet;
  const flow = fieldOf(world).scrollSpeed * Math.max(0.15, world.scroll);
  for (const g of world.gems) {
    const dx = p.x - g.x;
    const dy = p.y - g.y;
    const dz = -g.z;
    const d = Math.hypot(dx, dy, dz);
    if (g.pull || d < magnet) {
      g.pull = 1;
      const sp = 160 * world.scale;
      g.x += (dx / (d || 1)) * sp * dt;
      g.y += (dy / (d || 1)) * sp * dt;
      g.z += (dz / (d || 1)) * sp * dt;
    } else {
      g.z += flow * dt; // 前から流れてくる。上下左右は少しずつ自機へ寄る(ライト層向けに拾いやすく)
      const k = Math.min(1, config.xp.drift * dt);
      g.x += dx * k;
      g.y += dy * k;
    }
    if (d < config.player.radius + config.xp.gemRadius + 1) {
      g.taken = 1;
      gainXp(world, g.v);
    }
  }
}

export function xpToNext(level) {
  return config.xp.base + config.xp.step * (level - 1);
}

function gainXp(world, v) {
  const p = world.player;
  p.xp += v * world.params.xpGain;
  world.events.push({ k: "pick" });
  if (!world.pendingLevelUp && p.xp >= xpToNext(p.level)) {
    p.xp -= xpToNext(p.level);
    p.level++;
    world.pendingLevelUp = { choices: rollChoices(world, config.levelUp.choices) };
    world.events.push({ k: "levelUp", level: p.level });
  }
}

function cleanup(world) {
  const f = fieldOf(world);
  const behind = f.despawnBehindZ;
  const far = f.spawnFrontZ - 400 * world.scale;
  world.enemies = world.enemies.filter((e) => e.hp > 0 && e.z < behind && Math.abs(e.x) < 400 * world.scale);
  world.shots = world.shots.filter((s) => s.life > 0 && s.z > far && s.z < behind);
  world.enemyShots = world.enemyShots.filter((s) => s.life > 0 && s.z < behind);
  world.gems = world.gems.filter((g) => !g.taken && g.z < behind);
  world.repairs = world.repairs.filter((c) => !c.taken && c.z < behind);
  // 経験値がたまっていれば、次のレベルアップもすぐ出す
  const p = world.player;
  if (!world.pendingLevelUp && !world.over && p.xp >= xpToNext(p.level)) {
    p.xp -= xpToNext(p.level);
    p.level++;
    world.pendingLevelUp = { choices: rollChoices(world, config.levelUp.choices) };
    world.events.push({ k: "levelUp", level: p.level });
  }
  if (p.shield > maxShield(world)) p.shield = maxShield(world);
}

// 状態の指紋(リプレイの再計算が元と一致するかを確かめる。テスト用)
export function fingerprint(world) {
  const { events, ...rest } = world;
  const s = JSON.stringify(rest);
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0).toString(16);
}
